import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

const TOKEN_KEY = 'roster_token';
const USER_KEY = 'roster_user';

function parseJwtPayload(token) {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Restore session from localStorage
    const token = localStorage.getItem(TOKEN_KEY);
    const storedUser = localStorage.getItem(USER_KEY);

    if (token && storedUser) {
      // Verify token hasn't expired
      const payload = parseJwtPayload(token);
      if (payload && payload.exp * 1000 > Date.now()) {
        try {
          setUser(JSON.parse(storedUser));
        } catch {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
        }
      } else {
        // Token expired – clear storage
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
      }
    }
    setLoading(false);
  }, []);

  const loginOwner = useCallback((token, business) => {
    const payload = parseJwtPayload(token);
    const userData = {
      role: 'owner',
      business_id: payload.business_id,
      slug: payload.slug,
      name: business.name,
      email: business.email,
      logo_url: business.logo_url || null,
      brand_color: business.brand_color || '#2563eb',
    };
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(userData));
    setUser(userData);
  }, []);

  const loginStaff = useCallback((token, employee) => {
    const payload = parseJwtPayload(token);
    const userData = {
      role: 'staff',
      business_id: payload.business_id,
      employee_id: payload.employee_id,
      slug: payload.slug,
      name: employee.name,
      email: employee.email,
      employeeRole: employee.role,
      logo_url: employee.logo_url || null,
      brand_color: employee.brand_color || '#2563eb',
    };
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(userData));
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
  }, []);

  const getToken = useCallback(() => {
    return localStorage.getItem(TOKEN_KEY);
  }, []);

  const updateUser = useCallback((updates) => {
    setUser(prev => {
      const next = { ...prev, ...updates };
      localStorage.setItem(USER_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, loginOwner, loginStaff, logout, getToken, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
